import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getAccessToken } from '@/utils/jwt';

export type QcStatus = 'pass' | 'fail' | 'pending' | 'error';
export type QcSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info';
export type QcTrigger = 'manual' | 'pipeline' | 'scheduled';

export interface QcCheckResult {
  check_name: string;
  status: QcStatus;
  severity: QcSeverity;
  message: string;
  evidence: Record<string, unknown> | null;
}

export interface QcResult {
  id: string;
  project_id: string | null;
  entity_type: string;
  entity_id: number;
  entity_name: string | null;
  shot_name: string | null;
  department: string | null;
  status: QcStatus;
  checks: QcCheckResult[] | null;
  summary: string | null;
  triggered_by: Record<string, unknown> | null;
  agent_definition_id: number | null;
  job_id: number | null;
  slack_alert_sent: boolean;
  created_by_id: string | null;
  created_at: string | null;
  completed_at: string | null;
}

export interface QcSummary {
  total: number;
  pass: number;
  fail: number;
  pending: number;
  error: number;
  pass_rate: number;
  severity_breakdown: Record<string, number>;
  top_failures: { check_name: string; count: number }[];
  by_department: Record<string, { total: number; pass: number; fail: number }>;
}

interface QcResultsParams {
  project_id?: string;
  status?: QcStatus;
  shot_name?: string;
  department?: string;
  limit?: number;
  offset?: number;
}

export const qcApi = createApi({
  reducerPath: 'qcApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGENT_MCP_URL || '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['QcResult', 'QcSummary'],
  endpoints: (builder) => ({
    getQcResults: builder.query<QcResult[], QcResultsParams>({
      query: (params) => ({ url: '/qc/results', params }),
      providesTags: ['QcResult'],
    }),
    getQcResult: builder.query<QcResult, string>({
      query: (id) => `/qc/results/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'QcResult', id }],
    }),
    getQcSummary: builder.query<QcSummary, { project_id?: string } | undefined>({
      query: (params) => ({ url: '/qc/summary', params }),
      providesTags: ['QcSummary'],
    }),
  }),
});

export const { useGetQcResultsQuery, useGetQcResultQuery, useGetQcSummaryQuery } = qcApi;
